import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import {
  setIsCancelModalOpen,
  setJmrResults,
  setPageProgress,
  setSelectedTaskNames,
  setSelectedTasks,
  setCreatedAccommodationId,
  setBackgroundFormData,
  resetBackgroundFormData,
  setApprovalFormData,
  getPlacementDetails,
  setPlacementId,
  setJmrFlowType,
  setAccommodationSavingStatus,
  resetTldAlertModal,
  getEmployeeInfo,
  setRestrictionListItems,
  getRegionTasksById,
  setIsRestrictionsPageReadOnly,
  fetchAustinCases,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { CaseSources, formDataFromAustinCase, JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import { getAllowedSites } from 'src/store/siteSlice';
import { PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import { ApiCallStatus } from 'src/store/type';
import ProgressTracker from './progressTracker/progressTracker';
import BackgroundPageAUSTIN from './backgroundPage/backgroundPageAUSTIN';
import RestrictionsPage from './restrictionsPage/restrictionsPage';
import ResultsPage from './resultsPage/resultsPage';
import ReviewPage from './reviewPage/reviewPage';
import SendForApprovalPage from './sendForApprovalPage/sendForApprovalPage';
import { setupPreselectedRestrictionListItems } from 'src/utils/restrictionUtils';
import useOnDone from 'src/hooks/useOnDone';
import { createToast } from 'src/store/toastsSlice';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import './newJMR.scss';

/**
 * Determine whether to show the old or the new JMR experience.
 */
const NewJMR = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { caseId, login, type, placementId: placementIdParam, updateType } = useParams();
  const { isLoading } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    austinCases,
    areAustinCasesFetched,
    backgroundFormData,
    jmrFlowType,
    createdAccommodationId,
    pageProgress,
    isCancelModalOpen,
    consolidatedRegionTasks,
    selectedTasks,
    isLoadingGetPlacementDetails,
    isLoadingPAPI,
  } = useAppSelector((state) => state.newJmrPage);
  const placementId: number | undefined = !!placementIdParam ? Number(placementIdParam) : undefined;

  // "providerPlacedOffWork" will be handled by "providerPlacedOffWorkModal" modal
  useEffect(() => {
    if (createdAccommodationId > -1 && jmrFlowType != JmrFlowTypes.ProviderPlacedOffWork) {
      if (jmrFlowType !== JmrFlowTypes.CreateNewAccommodation) {
        navigate(`${PLACEMENT_DETAILS_BASEURL}/${placementId}`);
      } else {
        navigate(`${PLACEMENT_DETAILS_BASEURL}/${createdAccommodationId}`);
      }
      dispatch(setAccommodationSavingStatus(ApiCallStatus.Idle));
    }
  }, [createdAccommodationId]);

  // Set on the load/unload behavior
  useEffect(() => {
    // Ensure the latest site configurations are available without having to refresh the browser
    dispatch(getAllowedSites());
    if (placementId !== undefined) {
      dispatch(getPlacementDetails(placementId));
      dispatch(setPlacementId(placementId));
      dispatch(setJmrFlowType(updateType));
    } else {
      dispatch(setJmrFlowType(JmrFlowTypes.CreateNewAccommodation));
    }
    // Return
    return () => {
      dispatch(setPageProgress(JmrPageProgress.Background));
      dispatch(setSelectedTasks([]));
      dispatch(setSelectedTaskNames([]));
      dispatch(setJmrResults([]));
      // TODO background form data is now reset upon successful accommodation create/update. We should also move this
      // reset logic into the Cancel modal and other relevant locations to align with this state being managed by
      // business logic rather than this component's lifecycle (display logic)
      dispatch(resetBackgroundFormData());
      dispatch(setCreatedAccommodationId(-1));
      dispatch(setApprovalFormData({}));
      dispatch(setJmrFlowType(JmrFlowTypes.CreateNewAccommodation));
      dispatch(resetTldAlertModal());
      dispatch(setRestrictionListItems([]));
    };
  }, []);

  // Prefill data when access /newJMR/:type/:login/:caseId. This route is redirected
  // to if a link in Austin redirects to the placementDetails page, but no
  // placement exists. PlacementDatils routes here so the user is prompted to create a new placement.

  // pre-fill alias and auto-search/fill employee info from PAPI
  useEffect(() => {
    if (!!login) {
      dispatch(setBackgroundFormData({ alias: login }));
      dispatch(getEmployeeInfo(login));
    }
  }, [login]);

  // TODO setting caseSource based on type param carries a minor risk of privilege escelation because the user's
  // permissions are being checked in the useEffect of the child CaseSourceOption component which runs prior to the
  // execution of this useEffect (bottom up). This means we could theoretically set the case source to one the user is
  // not supposed to have access to. Ideally CaseSourceOption should be refactored to separate the business logic from
  // the display logic
  useEffect(() => {
    if (!!type) {
      if (Object.values(CaseSources).includes(type as CaseSources)) {
        dispatch(setBackgroundFormData({ caseSource: type }));
      } else {
        dispatch(
          createToast({
            type: 'error',
            message: `Invalid case source: ${type}`,
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    }
  }, [type]);

  useEffect(() => {
    if (type === CaseSources.Austin && !!login) {
      dispatch(fetchAustinCases({ alias: login }));
    }
  }, [type, login]);

  useEffect(() => {
    if (!!caseId) {
      if (type !== CaseSources.Austin) {
        // pre-fill Dali/Bali case text input
        dispatch(setBackgroundFormData({ caseID: caseId }));
      } else if (
        !isLoadingPAPI && // getEmployeeInfo overwrites backgroundFormData.siteLocation, wait for it to finish
        areAustinCasesFetched // wait until finished loading Austin cases for AA
      ) {
        // search cases, validate permissions, and pre-fill site and caseId selections
        dispatch(setBackgroundFormData(formDataFromAustinCase(caseId, austinCases, allowedSites, dispatch)));
      }
    }
  }, [caseId, type, isLoadingPAPI, areAustinCasesFetched]);

  // Fetch the "Austin Cases (incidents)". Note this is triggered by the ".getPlacementDetails"
  // function call above, so will only tigger if "placementId" in the URL is truthy.
  useOnDone(() => {
    const { caseIdValid } = backgroundFormData;
    if (backgroundFormData.caseSource === CaseSources.Austin && caseIdValid && !!placementId) {
      dispatch(fetchAustinCases({ placementId }));
    }
    // Reset caseID if invalid to force user to pick one.
    if (!caseIdValid) {
      dispatch(
        setBackgroundFormData({
          ...backgroundFormData,
          caseID: '',
        }),
      );
    }
  }, isLoadingGetPlacementDetails);

  // If updating a Placement, set the list of pre-selected items when the group of
  // selected-tasks change
  useEffect(() => {
    const { CompleteTransfer, UpdateDates, AlternativePlacementRecommended } = JmrFlowTypes;
    if (
      _.includes([CompleteTransfer, UpdateDates, AlternativePlacementRecommended], jmrFlowType) &&
      consolidatedRegionTasks.length > 0 &&
      selectedTasks.length > 0
    ) {
      const tasks = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
      dispatch(setRestrictionListItems(tasks));
    }
  }, [consolidatedRegionTasks, selectedTasks]);

  // Fetch tasks based on region id of the selected site. Note that the regionTasks
  // is used on the restrictions-pages for the list of tasks (aka restrictions).
  useEffect(() => {
    const v = backgroundFormData.site?.regionId;
    if (v !== undefined) {
      dispatch(getRegionTasksById(v));
    }
  }, [backgroundFormData.site?.regionId]);
  // TODO there is an eddge case where if the site object is not populate, the tasks are persisted accross sites as
  // stale state. this needs to be cleared when navigating away from the page.

  // List for flow-type updates
  useEffect(() => {
    // If updating dates, don't allow edits to the "Restrictions" page
    if (jmrFlowType === JmrFlowTypes.UpdateDates) {
      dispatch(setIsRestrictionsPageReadOnly(true));
    } else {
      dispatch(setIsRestrictionsPageReadOnly(false));
    }
    // If the user selected "Confirm" in the "Alternative Placement Modal" (the site decision
    // maker accepted an alternative placement) navigate to the "Job Matches" page.
    if (jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended) {
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    }
  }, [jmrFlowType]);

  return (
    <div className="new-jmr__page-wrapper">
      <LoadingSpinnerWrapper showSpinner={isLoading}>
        <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          {/* Main Content */}
          <Row spacingInset="400 none" alignmentHorizontal="center">
            <Box width="fit-content">
              <ProgressTracker />
            </Box>
          </Row>
          <Divider spacingAfter="400" />

          {/* Show the Different JMR pages based on progress */}
          {pageProgress === JmrPageProgress.Background ? (
            <BackgroundPageAUSTIN />
          ) : pageProgress === JmrPageProgress.Restrictions ? (
            <RestrictionsPage />
          ) : pageProgress === JmrPageProgress.JobMatches ? (
            <ResultsPage />
          ) : pageProgress === JmrPageProgress.Review ? (
            <ReviewPage />
          ) : pageProgress === JmrPageProgress.SendForApproval ? (
            <SendForApprovalPage />
          ) : null}
        </span>
      </LoadingSpinnerWrapper>

      {/* Modals */}
      <ConfirmModal
        width="419px"
        modalTitle="Leave page"
        modalDescription="Leaving this page will restart your Job Match Results process."
        open={isCancelModalOpen}
        setOpen={() => dispatch(setIsCancelModalOpen(false))}
        onConfirm={() => {
          dispatch(setRestrictionListItems([]));
          return navigate('/placementsOverview');
        }}
      />
    </div>
  );
};

// **** Export **** //

export default NewJMR;
